import React, { useState, useEffect } from 'react';
import { auth, db } from './config/firebaseConfig';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  addDoc,
  collection,
  serverTimestamp,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import './LoginScreen.css';
import Navbar from './Navbar';
import Footer from './Footer';

// Initialize Stripe
const stripePromise = loadStripe('pk_live_51QI6oqEIYpEP7XBnBKjyz85osVHW3ryWrv6TkUIixMEpEnhv9Jn8tEa7A7Fs7mCRfr0zgXzwUqDAfb8xxppixrc600gsnARSKW');

const TrialExpirationModal = ({
  isOpen,
  daysLeft,
  onClose,
  onUpgrade,
  trialEnded,
}) => {
  if (!isOpen) return null;

  const showEndedMessage = trialEnded || daysLeft <= 0;

  return (
    <div className='trial-modal-overlay'>
      <div className='trial-modal-content'>
        <h2>
          {showEndedMessage
            ? 'Your free trial has ended.'
            : `Your free trial ends in ${daysLeft} day${
                daysLeft > 1 ? 's' : ''
              }`}
        </h2>
        <p>
          {showEndedMessage
            ? 'Please upgrade to continue access.'
            : 'Consider upgrading to continue access.'}
        </p>
        {!showEndedMessage && <button onClick={onClose}>Later</button>}
        <button onClick={onUpgrade}>Upgrade now</button>
      </div>
    </div>
  );
};

// Password Recovery Modal Component
const PasswordRecoveryModal = ({ isOpen, onClose, onSubmit }) => {
  const [alias, setAlias] = useState('');
  const [step, setStep] = useState(1);
  const [recoveryQuestion, setRecoveryQuestion] = useState('');
  const [recoveryAnswer, setRecoveryAnswer] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userData, setUserData] = useState(null);

  const resetForm = () => {
    setAlias('');
    setStep(1);
    setRecoveryQuestion('');
    setRecoveryAnswer('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    setUserData(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const fetchRecoveryQuestion = async () => {
    setError('');
    
    if (!alias) {
      setError('Please enter your alias');
      return;
    }

    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('alias', '==', alias));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('No account found with this alias');
        return;
      }

      const userDoc = querySnapshot.docs[0];
      const user = userDoc.data();

      if (!user.recoveryEnabled) {
        setError('This account does not have recovery enabled');
        return;
      }

      setUserData({
        id: userDoc.id,
        ...user
      });
      setRecoveryQuestion(user.recoveryQuestion);
      setStep(2);
    } catch (err) {
      console.error('Error fetching recovery question:', err);
      setError('An error occurred. Please try again.');
    }
  };

  const verifyAnswer = () => {
    setError('');
    
    if (!recoveryAnswer) {
      setError('Please enter your answer');
      return;
    }

    if (userData && recoveryAnswer.toLowerCase().trim() === userData.recoveryAnswer.toLowerCase().trim()) {
      setStep(3);
    } else {
      setError('Incorrect answer. Please try again.');
    }
  };

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    
  const resetPassword = async () => {
    setError('');
    setSuccess('');
    
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Password validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      setError('Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character (ONLY @$!%*?&).');
      return;
    }

    try {
      // Submit the form data to parent component
      await onSubmit({
        userId: userData.id,
        systemEmail: userData.systemEmail,
        newPassword,
      });
      
      setSuccess('Password has been reset successfully!');
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('Error resetting password:', err);
      setError('An error occurred while resetting your password. Please try again.');
    }
  };

  if (!isOpen) return null;

return (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 9999
  }}>
    <div style={{
      backgroundColor: '#2e2e2e',
      padding: '30px',
      width: '90%',
      maxWidth: '400px',
      borderRadius: '10px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
      color: '#f0f0f0'
    }}>
      <h3 style={{ marginTop: 0, color: '#f09a73' }}>Account Recovery</h3>
      
      {step === 1 && (
        <div style={{ marginBottom: '20px' }}>
          <p>Enter your alias to recover your account:</p>
          <input 
            type="text" 
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            placeholder="Your alias"
            style={{
              width: '70%',
              padding: '12px',
              marginBottom: '15px',
              borderRadius: '8px',
              border: '1px solid #ccc',
              backgroundColor: '#575656',
              color: '#fff'
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button 
              onClick={fetchRecoveryQuestion}
              style={{
                backgroundColor: '#4caf50',
                padding: '12px 20px',
                borderRadius: '8px',
                color: '#fff',
                cursor: 'pointer',
                border: 'none'
              }}
            >Continue</button>
            <button 
              onClick={handleClose} 
              style={{
                backgroundColor: '#555',
                padding: '12px 20px',
                borderRadius: '8px',
                color: '#fff',
                cursor: 'pointer',
                border: 'none'
              }}
            >Cancel</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div style={{ marginBottom: '20px' }}>
          <p>Answer your security question:</p>
          <p style={{ fontWeight: 'bold', color: '#f09a73' }}>{recoveryQuestion}</p>
          <input 
            type="text" 
            value={recoveryAnswer}
            onChange={(e) => setRecoveryAnswer(e.target.value)}
            placeholder="Your answer"
            style={{
              width: '70%',
              padding: '12px',
              marginBottom: '15px',
              borderRadius: '8px',
              border: '1px solid #ccc',
              backgroundColor: '#575656',
              color: '#fff'
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button 
              onClick={verifyAnswer}
              style={{
                backgroundColor: '#4caf50',
                padding: '12px 20px',
                borderRadius: '8px',
                color: '#fff',
                cursor: 'pointer',
                border: 'none'
              }}
            >Verify Answer</button>
            <button 
              onClick={() => setStep(1)} 
              style={{
                backgroundColor: '#555',
                padding: '12px 20px',
                borderRadius: '8px',
                color: '#fff',
                cursor: 'pointer',
                border: 'none'
              }}
            >Back</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div style={{ marginBottom: '20px' }}>
          <p>Create a new password:</p>
          <input 
            type="password" 
            value={newPassword}
            onChange={(e) => {
				setNewPassword(e.target.value);
				// Clear any previous error when user starts typing again
				if (error && error.includes('Password must be')) {
				  setError('');
				}
			  }}
            placeholder="New password"
            style={{
              width: '70%',
              padding: '12px',
              marginBottom: '15px',
              borderRadius: '8px',
              border: '1px solid #ccc',
              backgroundColor: '#575656',
              color: '#fff'
            }}
          />
          {newPassword && !passwordRegex.test(newPassword) && (
			  <p style={{ color: '#ff6b6b', fontSize: '14px', marginTop: '-10px', marginBottom: '15px' }}>
				Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character (ONLY @$!%*?&).
			  </p>
			)}
          <input 
            type="password" 
            value={confirmPassword}
            onChange={(e) => {
				setConfirmPassword(e.target.value);
				// Clear mismatch error when user starts typing again
				if (error && error.includes('Passwords do not match')) {
				  setError('');
				}
			  }}
            placeholder="Confirm new password"
            style={{
              width: '70%',
              padding: '12px',
              marginBottom: '15px',
              borderRadius: '8px',
              border: '1px solid #ccc',
              backgroundColor: '#575656',
              color: '#fff'
            }}
          />
          {newPassword && confirmPassword && newPassword !== confirmPassword && (
			  <p style={{ color: '#ff6b6b', fontSize: '14px', marginTop: '-10px', marginBottom: '15px' }}>
				Passwords do not match.
			  </p>
			)}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button 
              onClick={resetPassword}
              style={{
                backgroundColor: '#4caf50',
                padding: '12px 20px',
                borderRadius: '8px',
                color: '#fff',
                cursor: 'pointer',
                border: 'none'
              }}
            >Reset Password</button>
            <button 
              onClick={() => setStep(2)} 
              style={{
                backgroundColor: '#555',
                padding: '12px 20px',
                borderRadius: '8px',
                color: '#fff',
                cursor: 'pointer',
                border: 'none'
              }}
            >Back</button>
          </div>
        </div>
      )}

      {error && <p style={{ color: '#ff6b6b', marginTop: '15px' }}>{error}</p>}
      {success && <p style={{ color: '#4caf50', marginTop: '15px' }}>{success}</p>}
    </div>
  </div>
);
};

const LoginScreen = ({ setTherapistId }) => {
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [daysLeft, setDaysLeft] = useState(0);
  const [userRole, setUserRole] = useState('');
  const [trialEnded, setTrialEnded] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [user, setUser] = useState(null);
  const [isRecoveryModalOpen, setIsRecoveryModalOpen] = useState(false);

  const togglePasswordVisibility = () => setIsPasswordVisible(prev => !prev);
  const navigate = useNavigate();

  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  const handleLoginIdentifierChange = e => {
    const value = e.target.value;
    setLoginIdentifier(value);
    setError(null);

    if (!value) {
      setLoginError('Login identifier is required.');
    } else {
      setLoginError('');
    }
  };

	const handlePasswordChange = e => {
	  const value = e.target.value;
	  setPassword(value);
	  setError(null);

	  if (!value) {
		setPasswordError('Password is required.');
	  } else if (!passwordRegex.test(value)) {
		setPasswordError(
		  'Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character (ONLY @$!%*?&).'
		);
	  } else {
		setPasswordError('');
	  }
	};

  const handleCloseModal = () => {
    setIsModalOpen(false);

    if (userRole === 'therapist') {
      navigate('/therapistdashboard');
    } else if (userRole === 'individual' || userRole === 'client') {
      navigate('/client-dashboard');
    }
  };

  const handleUpgrade = async () => {
    setIsModalOpen(false);

    try {
      const response = await fetch(
        'https://ocd-serenity.vercel.app/api/create-checkout-session',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            role: userRole,
            userId: user ? user.uid : null,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create checkout session.');
      }

      const data = await response.json();
      const stripe = await stripePromise;
      await stripe.redirectToCheckout({ sessionId: data.sessionId });
    } catch (error) {
      console.error('Error handling upgrade:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const logLoginEvent = async (loginIdentifier, role, eventType, note) => {
    try {
      await addDoc(collection(db, 'Logs'), {
        timestamp: serverTimestamp(),
        eventType,
        identifier: loginIdentifier,
        role,
        note,
      });
    } catch (error) {
      console.error('Error logging login event:', error);
    }
  };

  // Handle password recovery via alias and security question
  const handleRecoverySubmit = async (recoveryData) => {
    try {
      // Get user auth with admin SDK (not implemented here)
      // This would need to be implemented on your backend
      
      // For demonstration purposes, we'll assume it sends a request to your backend API
      const response = await fetch('https://ocd-serenity.vercel.app/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: recoveryData.userId,
          systemEmail: recoveryData.systemEmail,
          newPassword: recoveryData.newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
      	throw new Error(errorData.error || 'Failed to reset password');
      }

      return true;
    } catch (error) {
      console.error('Error in password recovery:', error);
      throw error;
    }
  };

  const handleLogin = async e => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // First, check if the login identifier is an alias
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('alias', '==', loginIdentifier));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // If no user found with this alias, check if it's a therapist email
        // (assuming therapists still use email-based login)
        const emailQuery = query(usersRef, where('email', '==', loginIdentifier));
        const emailSnapshot = await getDocs(emailQuery);

        if (emailSnapshot.empty) {
          setError('No account found with this login identifier.');
          setLoading(false);
          return;
        }

        // Email-based login for therapists
        const userDoc = emailSnapshot.docs[0];
        const userData = userDoc.data();

        // Proceed with normal email login
        const userCredential = await signInWithEmailAndPassword(
          auth,
          loginIdentifier,
          password
        );
        
        await handleSuccessfulLogin(userCredential.user, userData);
        return;
      }

      // Alias-based login for clients
      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();

      // Use the system email stored in Firestore for Firebase Auth
      const systemEmail = userData.systemEmail;
      
      // Authenticate with Firebase using the system email
      const userCredential = await signInWithEmailAndPassword(
        auth,
        systemEmail,
        password
      );

      await handleSuccessfulLogin(userCredential.user, userData);
    } catch (error) {
      console.error('Error logging in:', error);
      setError('Failed to log in. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulLogin = async (authUser, userData) => {
    setUser(authUser);
    const userRole = userData.role;
    const userStatus = userData.status || 'free';
    const createdAt = userData.createdAt ? userData.createdAt.toDate() : new Date();
    const daysElapsed = Math.floor(
      (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24)
    );

    setUserRole(userRole);

    // Log the login event in Firestore
    await logLoginEvent(
      userData.alias || userData.email,
      userRole,
      'login',
      'Logged in successfully!'
    );

    if (userRole === 'admin') {
      navigate('/admin-dashboard');
    } else if (userRole === 'client' || userRole === 'individual') {
      navigate('/client-dashboard');
    } else if (userRole === 'therapist') {
    /*
      const trialPeriodDays = 30;
      const remainingDays = trialPeriodDays - daysElapsed;
      setDaysLeft(remainingDays);

      if (
        remainingDays <= 10 &&
        remainingDays > 0 &&
        userStatus === 'free'
      ) {
        setTherapistId(authUser.uid);
        setIsModalOpen(true);
        setTrialEnded(false);
      } else if (remainingDays <= 0 && userStatus === 'free') {
        setTherapistId(authUser.uid);
        setIsModalOpen(true);
        setTrialEnded(true);
      } else if (userStatus === 'paid') {
        setTherapistId(authUser.uid);
        navigate('/therapistdashboard');
      } else if (userStatus === 'cancelled') {
        alert('The subscription for this account has been cancelled');
        setTherapistId(authUser.uid);
        setIsModalOpen(true);
        setTrialEnded(true);
      } else {
      */
        setTherapistId(authUser.uid);
        navigate('/therapistdashboard');
      //}
    }
  };

	const handlePasswordRecovery = () => {
	  // Check if the loginIdentifier is empty
	  if (!loginIdentifier) {
		setError('Please enter your email or username before requesting password recovery.');
		return;
	  }
	  
	  // Check if it looks like an email (therapist case)
	  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	  if (emailRegex.test(loginIdentifier)) {
		// It's an email, use Firebase's built-in password reset
		sendPasswordResetEmail(auth, loginIdentifier)
		  .then(() => {
		    setMessage('Password reset email sent! Check your inbox.');
		    setError(null);
		  })
		  .catch((error) => {
		    console.error('Error sending password reset email:', error);
		    setError('Failed to send password reset email. Please try again.');
		  });
	  } else {
		// It's not an email, must be an alias, open the recovery modal
		setIsRecoveryModalOpen(true);
	  }
	};

  // Legacy email-based password reset for therapists and admins
  const handlePasswordReset = async () => {
    if (!loginIdentifier) {
      setError('Please enter your email address to reset the password.');
      return;
    }
    
    // Check if input is an email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(loginIdentifier)) {
      setIsRecoveryModalOpen(true); // Open recovery modal for alias-based accounts
      return;
    }
    
    setMessage('');
    setError(null);

    try {
      await sendPasswordResetEmail(auth, loginIdentifier);
      setMessage('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setError('Failed to send password reset email. Please try again.');
    }
  };

  return (
    <>
      <Navbar />
      <TrialExpirationModal
        isOpen={isModalOpen}
        daysLeft={daysLeft}
        onClose={handleCloseModal}
        onUpgrade={handleUpgrade}
        trialEnded={trialEnded}
      />
      <PasswordRecoveryModal
        isOpen={isRecoveryModalOpen}
        onClose={() => setIsRecoveryModalOpen(false)}
        onSubmit={handleRecoverySubmit}
      />
      <div className='login-screen'>
        <h2>Login</h2>
        <form onSubmit={handleLogin} className='login-form'>
          <label>
            Username or Email:
            <input
              type='text'
              value={loginIdentifier}
              onChange={handleLoginIdentifierChange}
              required
              placeholder="Enter your alias or email"
            />
            {loginError && <p className='error'>{loginError}</p>}
          </label>
          <label>
            Password:
            <div style={{ position: 'relative' }}>
              <input
                type={isPasswordVisible ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                required
              />
              <span
                onClick={togglePasswordVisibility}
                style={{
                  position: 'absolute',
                  right: '25px',
                  top: '50%',
                  fontSize: '25px',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                }}
              >
                {isPasswordVisible ? '👁️' : '🙈'}
              </span>
            </div>
            {passwordError && <p className='error'>{passwordError}</p>}
          </label>
          <button
            type='submit'
            disabled={!!loginError || !!passwordError || loading || !loginIdentifier || !password}
          >
            {loading ? 'Logging in...' : 'Log In'}
          </button>
          <br />
          <br />
          <a
            href='#'
            onClick={handlePasswordRecovery}
            className='reset-password-link'
            style={{
              textDecoration: 'underline',
              color: 'white',
              cursor: 'pointer',
            }}
          >
            Forgot Password?
          </a>
          {message && <p className='message'>{message}</p>}
          {error && <p className='error'>{error}</p>}
        </form>
      </div>
      <Footer />
    </>
  );
};

export default LoginScreen;